import React, { FC, useEffect, useMemo, useRef } from 'react';

import { useFormattedUser } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import { warn } from '@adsk/alloy-react-helpers';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import AttachmentsViewerDetail from './AttachmentsViewerDetail';
import formatCreatedOn from '../../helpers/formatCreatedOn';
import formatFileSize from '../../helpers/formatFileSize';
import { useTranslation } from '../../i18n';

type AttachmentsViewerDetailsProps = {
  createdOn: string;
  fileSize: number;
  createdByName?: string;
  createdBy?: string;
};

const AttachmentsViewerDetails: FC<AttachmentsViewerDetailsProps> = ({
  createdOn,
  fileSize,
  createdByName,
  createdBy,
}) => {
  const { t } = useTranslation();
  const { locale } = useOnlyLocale();

  const showCreatedByWarn = useRef<boolean>(false);

  const { formatDisplayUser } = useFormattedUser();
  const createdByDisplayName = useMemo(() => {
    return createdBy ? formatDisplayUser(createdBy).displayName : createdByName;
  }, [createdBy, createdByName, formatDisplayUser]);

  const details = useMemo(
    () => [
      {
        label: t('ADDED_ON'),
        value: formatCreatedOn(createdOn, locale),
      },
      {
        label: t('ADDED_BY'),
        value: createdByDisplayName,
      },
      {
        label: t('FILE_SIZE'),
        value: formatFileSize(fileSize),
      },
    ],
    [t, createdOn, createdByDisplayName, fileSize, locale],
  );

  useEffect(() => {
    if (!showCreatedByWarn.current && !createdBy && createdByName) {
      warn(
        'AttachmentsViewerDetails - passing createdByName without createdBy is deprecated, please make sure to pass createdBy as well',
      );
      showCreatedByWarn.current = true;
    }
  }, [createdBy, createdByName]);
  return (
    <Box
      height="100%"
      width="380px"
      padding="16px"
      borderLeft={`1px solid ${theme.colors.charcoal200}`}
    >
      <Box typography="heading2" marginBottom="12px">
        {t('DETAILS')}
      </Box>
      {details.map(
        (data) =>
          data.value && (
            <AttachmentsViewerDetail
              label={data.label as string}
              value={data.value}
              key={data.label as string}
            />
          ),
      )}
    </Box>
  );
};

export default AttachmentsViewerDetails;
